import React, { useEffect, useState } from 'react';
import { BanknotesIcon, CalendarIcon, ChevronLeftIcon, CreditCardIcon, PrinterIcon, CubeIcon } from '@heroicons/react/16/solid';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AppUser } from '../../../../models/AppUser';
import { useAppUser } from '../../../../hooks/useAppUser';
import showErrorToast from '../../../../components/Toasts/ShowErrorToast';
import { AppUserService } from '../../../../services/appUserService';
import showSuccessToast from '../../../../components/Toasts/ShowSuccessToast';
import Loading from '../../../../components/Loading';
import { Avatar } from '../../../../components/Avatar';
import { Heading, Subheading } from '../../../../components/Heading';
import { Badge } from '../../../../components/Badge';
import dateFormat from 'dateformat';
import { Button } from '../../../../components/Button';
import { DescriptionDetails, DescriptionList, DescriptionTerm } from '../../../../components/Description-List';
import { Card } from '../../../../components/Card';
import { Dialog, DialogActions, DialogDescription, DialogTitle } from '../../../../components/Dialog';
import { AppTrackLocation } from '../../../../models/AppTrackLocation';
import { useAppTrackLocations } from '../../../../hooks/useAppTrackLocation';
import { useAppPreCallPlans } from '../../../../hooks/useAppPreCallPlan';
import { PreCallPlan } from '../../../../models/AppPreCallPlan';
import { generatePreCallPlanPDF, printPDF } from '../../../../utils/pdfUtils';

const ViewAppPreCallPlanIdPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [plan, setPlan] = useState<PreCallPlan | null>(null);
    const navigate = useNavigate();
    const { fetchPreCallPlanById, loading, error, } = useAppPreCallPlans();

    useEffect(() => {
        document.title = "View App Pre Call Plan";
    }, []);

    useEffect(() => {
        if (!id) return;

        const fetchPlanDetails = async () => {
            try {
                const fetchedPlan = await fetchPreCallPlanById(Number(id));
                setPlan(fetchedPlan!);
            } catch (err) {
                console.error('Failed to fetch pre call plan details:', err);
            }
        };

        fetchPlanDetails();
    }, [id]);

    const handlePrintReport = () => {
        if (!plan) {
            showErrorToast('No plan data available for printing');
            return;
        }

        try {
            const doc = generatePreCallPlanPDF(plan);
            const filename = `PreCallPlan_${plan.id}_${new Date().toISOString().split('T')[0]}.pdf`;
            printPDF(doc, filename);
            showSuccessToast('PDF report generated successfully');
        } catch (error) {
            console.error('Error generating PDF:', error);
            showErrorToast('Failed to generate PDF report');
        }
    };

    if (loading) return <Loading />;
    if (error) return <p className="py-16 text-center text-red-700">{error}</p>;
    if (!plan) return <p className="py-16 text-center">Pre Call Plan not found.</p>;

    return (
        <>
            <div className="max-lg:hidden">
                <Link to="/gp/app/precallplans" className="inline-flex items-center gap-2 text-sm/6 text-sm/6 text-zinc-500 dark:text-zinc-400">
                    <ChevronLeftIcon className="size-4 fill-zinc-400 dark:fill-zinc-500" />
                    Pre Call Plans
                </Link>
            </div>
            <div className="mt-4 lg:mt-8">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-4">
                        <Avatar square initials={plan.salesman_username![0]} className="bg-zinc-300 text-gray-700 size-8" />
                        <Heading>{plan.salesman_username}</Heading>
                    </div>
                    <Button onClick={handlePrintReport} className="flex items-center gap-2">
                        <PrinterIcon className="h-4 w-4" />
                        Print Report
                    </Button>
                </div>
                <div className="isolate mt-2.5 flex flex-wrap justify-between gap-x-6 gap-y-4">
                    <div className="flex flex-wrap gap-x-10 gap-y-4 py-1.5">
                        <span className="flex items-center gap-2 text-base/6 text-zinc-950 sm:text-sm/6 dark:text-white">
                            <CalendarIcon className="size-4 shrink-0 fill-zinc-400 dark:fill-zinc-500" />
                            <span>{dateFormat(plan.created_time, "mmmm dd, yyyy")}</span>
                        </span>
                    </div>
                </div>
            </div>

            <Card className="mt-12 p-6">
                <Subheading>Pre Call Plan</Subheading>
                <DescriptionList className="mt-4">
                    <DescriptionTerm>Salesman</DescriptionTerm>
                    <DescriptionDetails>{plan.salesman_username || '-'}</DescriptionDetails>
                    <DescriptionTerm>Customer</DescriptionTerm>
                    <DescriptionDetails>{plan.customer_fullname || '-'}</DescriptionDetails>
                    <DescriptionTerm>Key Message</DescriptionTerm>
                    <DescriptionDetails>{plan.key_message || '-'}</DescriptionDetails>
                    <DescriptionTerm>Buying Indicator</DescriptionTerm>
                    <DescriptionDetails>{plan.buying_indicator || '-'}</DescriptionDetails>
                    <DescriptionTerm>Collection</DescriptionTerm>
                    <DescriptionDetails>{plan.collection || '-'}</DescriptionDetails>
                    <DescriptionTerm>Competition Presence</DescriptionTerm>
                    <DescriptionDetails>{plan.competition_presence || '-'}</DescriptionDetails>
                    <DescriptionTerm>Contact Feedback</DescriptionTerm>
                    <DescriptionDetails>{plan.contact_feedback || '-'}</DescriptionDetails>
                </DescriptionList>
            </Card>

            <Card className="mt-12 p-6">
                <Subheading>Products</Subheading>

                {plan.precallplanproducts && plan.precallplanproducts.length > 0 ? (
                    <div className="space-y-4 mt-4">
                        {plan.precallplanproducts.map((product: any, index: number) => (
                            <div key={product.id || index} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                                <div className="flex items-start gap-3">
                                    <div className="flex-shrink-0">
                                        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                                            <CubeIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                        </div>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between">
                                            <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                                                Product #{index + 1}
                                            </h4>
                                            <Badge color="blue">ID: {product.product_id}</Badge>
                                        </div>
                                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                                            {product.description || 'No description available'}
                                        </p>
                                        {product.remarks && (
                                            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                                <span className="font-medium">Remarks:</span> {product.remarks}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <CubeIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No products</h3>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            No products have been added to this pre-call plan.
                        </p>
                    </div>
                )}
            </Card>
        </>
    );
};

export default ViewAppPreCallPlanIdPage;
